/**
 * Pantalla siempre encendida mientras tocas (§9.5).
 *
 * - Donde existe Screen Wake Lock (Android, Windows), se usa.
 * - En Safari 15 no existe: la app reproduce en bucle un vídeo diminuto e invisible, lo que
 *   impide que el iPad se bloquee. Hay que arrancarlo desde un toque.
 *
 * WebKit solo impide el bloqueo si el vídeo avanza, no está en bucle, no está silenciado y
 * tiene pista de audio (HTMLMediaElement::shouldDisableSleep y computeCanProduceAudio). Por eso:
 * vídeo con pista de audio silenciosa, sin silenciar, sin «loop», fuera de la pantalla y con
 * saltos de posición para que nunca llegue al final, como hace NoSleep.js en iOS.
 *
 * En el iPad, el vídeo mudo y el vídeo con «loop» dejaron que la pantalla se apagara a los
 * 5 minutos (2 de octubre).
 */
import { KEEP_AWAKE_VIDEO_WITH_AUDIO } from './media'

export type KeepAwakeMethod = 'wake-lock' | 'video'
export type KeepAwakeRequest = 'auto' | KeepAwakeMethod

interface WakeLockSentinelLike {
  release(): Promise<void>
  addEventListener(type: 'release', listener: () => void): void
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>
}

function wakeLockApi(): WakeLockLike | null {
  const api = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock
  return api ?? null
}

export function hasWakeLock(): boolean {
  return wakeLockApi() !== null
}

let method: KeepAwakeMethod | null = null
let sentinel: WakeLockSentinelLike | null = null
let video: HTMLVideoElement | null = null
let startedAt = 0
const events: string[] = []
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((listener) => listener())
}

/** Anota lo que le pasa al método activo (pausas, reanudaciones, errores) para el spike. */
function record(message: string) {
  const seconds = Math.round((Date.now() - startedAt) / 1000)
  const entry = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} ${message}`
  events.push(entry)
  if (events.length > 200) events.shift()
  console.info(`Pantalla encendida: ${entry}`)
  notify()
}

export function keepAwakeEvents(): readonly string[] {
  return events
}

async function requestWakeLock(): Promise<void> {
  const api = wakeLockApi()
  if (!api) throw new Error('Sin Wake Lock')
  sentinel = await api.request('screen')
  sentinel.addEventListener('release', () => record('Wake Lock liberado'))
}

function createVideo(): HTMLVideoElement {
  const element = document.createElement('video')
  element.src = KEEP_AWAKE_VIDEO_WITH_AUDIO
  // Ni «loop» ni «muted»: cualquiera de los dos deja que iOS apague la pantalla.
  element.loop = false
  element.muted = false
  element.playsInline = true
  element.setAttribute('title', 'Atril')
  element.setAttribute('playsinline', '')
  element.setAttribute('webkit-playsinline', '')
  element.setAttribute('aria-hidden', 'true')
  // En iOS el vídeo tiene que estar en el documento, pero puede quedar fuera de la pantalla.
  Object.assign(element.style, {
    position: 'absolute',
    left: '-100%',
    top: '-100%',
    width: '64px',
    height: '64px',
    pointerEvents: 'none',
  })
  // Nunca llega al final: al pasar de medio segundo vuelve a una posición al azar.
  element.addEventListener('timeupdate', () => {
    if (element.currentTime > 0.5) element.currentTime = Math.random() * 0.5
  })
  element.addEventListener('pause', () => {
    if (video !== element) return
    record('vídeo pausado')
    if (document.visibilityState === 'visible') {
      element.play().then(
        () => record('vídeo reanudado'),
        (error: unknown) => record(`no se pudo reanudar: ${String(error)}`),
      )
    }
  })
  element.addEventListener('ended', () => {
    if (video !== element) return
    record('vídeo terminado; se reinicia')
    element.currentTime = 0
    void element.play()
  })
  element.addEventListener('error', () => record('error del vídeo'))
  document.body.appendChild(element)
  return element
}

/** Al volver a primer plano el Wake Lock se ha liberado y el vídeo se ha pausado: se reanudan. */
function onVisibilityChange() {
  if (!method) return
  record(document.visibilityState === 'visible' ? 'app visible' : 'app oculta')
  if (document.visibilityState !== 'visible') return
  if (method === 'wake-lock') {
    requestWakeLock().catch((error) => console.warn('No se pudo renovar el Wake Lock', error))
  } else if (video?.paused) {
    video.play().catch((error) => console.warn('No se pudo reanudar el vídeo', error))
  }
}

/** Activa la pantalla encendida. Llamar desde un toque. */
export async function startKeepAwake(request: KeepAwakeRequest = 'auto'): Promise<KeepAwakeMethod> {
  stopKeepAwake()
  const chosen: KeepAwakeMethod =
    request === 'auto' ? (hasWakeLock() ? 'wake-lock' : 'video') : request
  startedAt = Date.now()
  events.length = 0
  if (chosen === 'wake-lock') {
    await requestWakeLock()
  } else {
    video = createVideo()
    await video.play()
  }
  method = chosen
  document.addEventListener('visibilitychange', onVisibilityChange)
  record(`activado (${chosen})`)
  return chosen
}

export function stopKeepAwake(): void {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  sentinel?.release().catch(() => {})
  sentinel = null
  if (video) {
    const element = video
    video = null
    element.pause()
    element.remove()
  }
  if (method) {
    method = null
    notify()
  }
}

export function keepAwakeMethod(): KeepAwakeMethod | null {
  return method
}

export function subscribeKeepAwake(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
